import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import { getNestedValue } from "./utils";
import MultiSelectApiPopup from "./MultiSelectPopup";
import ArrayFieldRenderer from "./ArrayFieldRenderer";
import SignatureCanvas from "react-signature-canvas";
import "./FieldRenderer.css";

/* -------------------- Utilities (stable) -------------------- */
const decodeHtml = (s) => {
  if (typeof s !== "string") return s;
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};

/* ============================================================
   SafetyChecklistWithPermit (hoisted + memoized)
   ============================================================ */

function areSafetyChecklistPropsEqual(prev, next) {
  if (prev.baseField !== next.baseField) return false;
  if (prev.col.api !== next.col.api) return false;
  if (prev.col.label !== next.col.label) return false;
  if ((prev.col.fieldDependenton || "") !== (next.col.fieldDependenton || ""))
    return false;

  const depKeyPrev = prev.col.fieldDependenton?.split(".")[0] || "permitTypes";
  const depKeyNext = next.col.fieldDependenton?.split(".")[0] || "permitTypes";
  const prevDepVal = prev.formData?.[depKeyPrev];
  const nextDepVal = next.formData?.[depKeyNext];
  if (JSON.stringify(prevDepVal) !== JSON.stringify(nextDepVal)) return false;

  const prevSelf = prev.formData?.[prev.baseField];
  const nextSelf = next.formData?.[next.baseField];
  if (JSON.stringify(prevSelf) !== JSON.stringify(nextSelf)) return false;

  const prevOpts = prev.dropdownOptions?.[prev.col.api];
  const nextOpts = next.dropdownOptions?.[next.col.api];
  if (JSON.stringify(prevOpts) !== JSON.stringify(nextOpts)) return false;

  return true;
}

const SignatureField = React.memo(function SignatureField({
  label,
  value,
  onChange,
  uploadApi,
  folder = "signatures",
}) {
  const sigRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [broken, setBroken] = React.useState(false);

  const clearPad = () => sigRef.current?.clear();

  const saveSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert("Please sign before saving.");
      return;
    }
    const dataUrl = sigRef.current.toDataURL("image/png");
    try {
      setUploading(true);
      const blob = await (await fetch(dataUrl)).blob();
      const randomName = `signature_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 8)}.png`;
      const file = new File([blob], randomName, { type: "image/png" });

      const fd = new FormData();
      fd.append("image", file);
      if (folder) fd.append("folder", folder);

      const res = await fetch(uploadApi, { method: "POST", body: fd });
      const text = await res.text().catch(() => "");
      let json = {};
      try {
        json = text ? JSON.parse(text) : {};
      } catch {}
      if (!res.ok)
        throw new Error(
          json?.message || json?.error || text || "Upload failed"
        );

      const newUrl =
        json.fileUrl ||
        json.url ||
        (Array.isArray(json.urls) && json.urls[0]) ||
        (/^https?:\/\//i.test(text.trim()) ? text.trim() : "");

      if (!newUrl) throw new Error("Upload succeeded but no URL returned");
      onChange(newUrl);
    } catch (e) {
      alert(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {value ? (
        <div className="image-preview-container" style={{ marginBottom: 12 }}>
          <img
            src={value}
            alt="Signature"
            className="image-preview"
            style={{
              maxWidth: "100%",
              maxHeight: 150,
              borderRadius: 6,
              objectFit: "contain",
              border: "1px solid #ddd",
              background: "#fff",
            }}
            onError={() => setBroken(true)}
            onLoad={() => setBroken(false)}
          />
          <div style={{ marginTop: 4 }}>
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb" }}
            >
              View Full Signature
            </a>
            {broken && (
              <span style={{ marginLeft: 12, color: "#d33" }}>
                (Image failed to load)
              </span>
            )}
          </div>
        </div>
      ) : (
        <p style={{ color: "gray" }}>No signature saved yet</p>
      )}
      <SignatureCanvas
        ref={sigRef}
        penColor="black"
        canvasProps={{
          width: 400,
          height: 150,
          className: "signature-canvas",
          style: {
            border: "1px solid #ccc",
            borderRadius: 6,
            background: "#fff",
          },
        }}
      />
      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <button
          type="button"
          className="btn-editForm"
          onClick={clearPad}
          disabled={uploading}
        >
          Clear
        </button>
        <button
          type="button"
          className="btn-editForm btn-editForm-primary"
          onClick={saveSignature}
          disabled={uploading}
        >
          {uploading ? "Saving…" : "Save Signature"}
        </button>
      </div>
    </div>
  );
});


const SafetyChecklistWithPermit = memo(function SafetyChecklistWithPermit({
  col,
  formData,
  onChange,
  baseField,
  dropdownOptions,
}) {
  const [items, setItems] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /* ---------- normalize to plain (always includes question) ---------- */
  const toPlain = useCallback(
    (arr) =>
      (arr || []).map((it) => {
        const text = it.question ?? it.CheckPoints ?? it.title ?? "Untitled";
        const out = {
          title: it.title ?? text,
          question: it.question ?? text, // ✅ backend requirement guaranteed
          response: it.response ?? "N/A",
        };
        col.subFields?.forEach((sf) => {
          if (!["title", "question", "response"].includes(sf.field)) {
            out[sf.field] = it[sf.field] ?? "";
          }
        });
        return out;
      }),
    [col.subFields]
  );

  /* ---------- dependent field ---------- */
  const depKey = col.fieldDependenton?.split(".")[0] || "permitTypes";
  const depValue = formData?.[depKey];

  const selectedKey = useMemo(() => {
    const objId =
      typeof depValue === "object" && depValue?._id
        ? depValue._id
        : typeof depValue === "string" && /^[a-f\d]{24}$/i.test(depValue)
        ? depValue
        : null;

    if (objId) return `id:${objId}`;
    const nestedName =
      formData?.permitTypes?.permitsType ||
      formData?.checklistName ||
      formData?.name ||
      (typeof depValue === "string" ? depValue : null);

    if (nestedName) return `name:${String(nestedName).trim()}`;
    return null;
  }, [depValue, formData]);

  const lastAppliedKeyRef = useRef(null);
  const ddOptions = dropdownOptions?.[col.api];

  /* ---------- load list ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        if (Array.isArray(ddOptions) && ddOptions.length) {
          if (!cancelled) setList(ddOptions);
          return;
        }
        const res = await fetch(col.api);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [data].filter(Boolean);
        if (!cancelled) setList(arr);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ddOptions, col.api]);

  /* ---------- apply checklist items ---------- */
  /* ---------- apply checklist items ---------- */
  useEffect(() => {
    let cancelled = false;

    // --- 1) Edit mode: use backend-provided items ---
    const existing = Array.isArray(formData?.[baseField])
      ? formData[baseField]
      : [];

    if (existing.length > 0) {
      const normalized = toPlain(existing);
      if (!cancelled) {
        setItems(normalized);
        lastAppliedKeyRef.current = "fromBackend"; // ✅ mark edit mode
      }
      return; // ⛔ stop here, don’t override with fieldDependenton
    }

    // --- 2) New form: build from dependent selection ---
    if (selectedKey === lastAppliedKeyRef.current && items.length) return;

    if (!selectedKey) {
      if (lastAppliedKeyRef.current !== null) {
        lastAppliedKeyRef.current = null;
        setItems([]);
      }
      return;
    }

    const [by, val] = selectedKey.split(":");
    const match = list.find((p) => {
      if (by === "id") return p?._id === val;
      if (by === "name")
        return (
          String(p?.permitsType || p?.checklistName || p?.name || "").trim() ===
          val
        );
      return false;
    });
    if (!match) return;

    let apiChecks = [];
    if (Array.isArray(match?.SafetyChecks)) apiChecks = match.SafetyChecks;
    else if (Array.isArray(match?.items)) apiChecks = match.items;

    const merged = (apiChecks || []).map((it, idx) => {
      const text =
        it?.CheckPoints ?? it?.question ?? it?.title ?? `Untitled ${idx + 1}`;
      const obj = {
        _id: it?._id || text,
        title: it?.title ?? text,
        question: it?.question ?? text,
        response: it?.response ?? it?.defaultResponse ?? "N/A",
      };
      col.subFields?.forEach((sf) => {
        if (!["title", "question", "response"].includes(sf.field)) {
          obj[sf.field] = it[sf.field] ?? "";
        }
      });
      return obj;
    });

    if (!cancelled) {
      setItems(merged);
      lastAppliedKeyRef.current = selectedKey; // ✅ mark new form mode
    }

    return () => {
      cancelled = true;
    };
  }, [selectedKey, list, baseField, col.subFields, toPlain]);

  const lastPushedRef = useRef("");


  /* ---------- push normalized to parent ---------- */
useEffect(() => {
  const normalized = toPlain(items);
  const json = JSON.stringify(normalized);

  // 🚫 prevent infinite loop: only push upward if actual change happened
  if (json !== lastPushedRef.current) {
    lastPushedRef.current = json;
    onChangeRef.current(baseField, normalized);
  }
}, [items, baseField, toPlain]);


  /* ---------- handlers ---------- */
  const handleChange = useCallback((idx, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }, []);

  /* ---------- response options ---------- */
  const responseOptions = col.subFields?.find((sf) => sf.field === "response")
    ?.options || ["Yes", "No", "N/A"];

  /* ---------- render ---------- */
  if (loading) return <p>Loading…</p>;
  if (error) return <p style={{ color: "#b00020" }}>{error}</p>;
  if (!selectedKey) return <p style={{ color: "#666" }}>Select a type.</p>;

  return (
    <div className="form-group">
      <label className="form-label">{col.label}</label>
      <table
        className="table"
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr>
            {col.subFields.map((sf) => (
              <th key={sf.field} style={{ textAlign: "left", padding: 8 }}>
                {sf.label || sf.field}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it._id ?? idx}>
              {col.subFields.map((sf) => {
                const val = it[sf.field];

                // ----- handle dropdowns -----
                if (sf.type === "dropdown" && Array.isArray(sf.options)) {
                  return (
                    <td key={sf.field} style={{ textAlign: "center" }}>
                      <select
                        value={val}
                        onChange={(e) =>
                          handleChange(idx, sf.field, e.target.value)
                        }
                        className="form-input"
                      >
                        {sf.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                  );
                }

                // ----- handle editable text fields (title, question, or others) -----
                if (sf.field === "title" || sf.field === "question") {
                  return (
                    <td key={sf.field}>
                      <input
                        type="text"
                        value={val || ""}
                        onChange={(e) =>
                          handleChange(idx, sf.field, e.target.value)
                        }
                        className="form-input"
                      />
                    </td>
                  );
                }

                // ----- fallback: display as text -----
                return <td key={sf.field}>{val || "—"}</td>;
              })}
            </tr>
          ))}

          {items.length === 0 && (
            <tr>
              <td
                colSpan={col.subFields.length}
                style={{ padding: 8, color: "#666" }}
              >
                No checklist items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
},
areSafetyChecklistPropsEqual);


  const WorkpermitAdditionField = React.memo(function WorkpermitAdditionField({
    col,
    formData,
    onChange,
  }) {
    const currentValue = Array.isArray(formData[col.field])
      ? formData[col.field]
      : [];

    // ---- Fetch permit type options from API ----
    const permitApi = col.subFields?.[0]?.api || ""; // e.g. /permitstype
    const permitApiTitle = col.subFields?.[0]?.apiTitle || "name"; // e.g. 'permitsType'

    const [permitOptions, setPermitOptions] = React.useState([]);
    const [permitLoading, setPermitLoading] = React.useState(false);
    const [permitError, setPermitError] = React.useState("");

    React.useEffect(() => {
      let cancelled = false;
      if (!permitApi) return;
      (async () => {
        try {
          setPermitLoading(true);
          setPermitError("");
          const res = await fetch(permitApi, { method: "GET" });
          const data = await res.json();
          const arr = Array.isArray(data) ? data : [data].filter(Boolean);
          if (!cancelled) setPermitOptions(arr);
        } catch (e) {
          if (!cancelled)
            setPermitError(e?.message || "Failed to load permit types");
        } finally {
          if (!cancelled) setPermitLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [permitApi]);

    // ---- helpers ----
    const handleUpdate = (index, field, val) => {
      const updated = [...currentValue];
      updated[index] = { ...updated[index], [field]: val };
      onChange(col.field, updated);
    };

    const handleAddPermitType = () => {
      onChange(col.field, [
        ...currentValue,
        { permitType: "", PageFields: [] },
      ]);
    };

    const handleRemovePermitType = (idx) => {
      const updated = currentValue.filter((_, i) => i !== idx);
      onChange(col.field, updated);
    };

    const handleAddPageField = (permitIdx) => {
      const updated = [...currentValue];
      const pf = updated[permitIdx].PageFields || [];
      pf.push({ Title: "", Headers: "", Type: "", Options: [] });
      updated[permitIdx].PageFields = pf;
      onChange(col.field, updated);
    };

    const handleRemovePageField = (permitIdx, pageIdx) => {
      const updated = [...currentValue];
      const pf = updated[permitIdx].PageFields || [];
      pf.splice(pageIdx, 1);
      updated[permitIdx].PageFields = pf;
      onChange(col.field, updated);
    };

    // Normalize selected value (supports id string or populated object)
    const getSelectedPermitId = (permitTypeVal) => {
      if (!permitTypeVal) return "";
      if (typeof permitTypeVal === "string") return permitTypeVal; // ObjectId as string
      if (typeof permitTypeVal === "object" && permitTypeVal._id)
        return permitTypeVal._id; // populated
      return "";
    };

    // Types list for PageFields.Type
    const fieldTypeOptions = col.subFields?.[1]?.subFields?.[2]?.options || [];

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        {permitError && (
          <div style={{ color: "#b00020", marginBottom: 8 }}>{permitError}</div>
        )}

        {currentValue.map((permit, idx) => {
          const selectedId = getSelectedPermitId(permit.permitType);

          return (
            <div
              key={idx}
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                background: "#fafafa",
              }}
            >
              {/* Permit Type Dropdown */}
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                >
                  Permit Type
                </label>
                <select
                  className="form-input"
                  value={selectedId}
                  onChange={(e) =>
                    handleUpdate(idx, "permitType", e.target.value)
                  }
                  disabled={permitLoading}
                >
                  <option value="">-- Select Permit Type --</option>
                  {permitOptions.map((opt) => (
                    <option key={opt._id} value={opt._id}>
                      {opt[permitApiTitle] ?? opt.name ?? "(unnamed)"}
                    </option>
                  ))}
                </select>
                {permitLoading && (
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    Loading permit types…
                  </div>
                )}
              </div>

              {/* PageFields Section */}
              <div>
                <label
                  style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                >
                  Page Fields
                </label>

                {(permit.PageFields || []).map((pf, pfIdx) => (
                  <div
                    key={pfIdx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    {/* Title */}
                    <input
                      type="text"
                      placeholder="Title"
                      className="form-input"
                      value={pf.Title}
                      onChange={(e) =>
                        handleUpdate(idx, "PageFields", [
                          ...permit.PageFields.slice(0, pfIdx),
                          { ...pf, Title: e.target.value },
                          ...permit.PageFields.slice(pfIdx + 1),
                        ])
                      }
                    />

                    {/* Headers */}
                    <input
                      type="text"
                      placeholder="Header Key"
                      className="form-input"
                      value={pf.Headers}
                      onChange={(e) =>
                        handleUpdate(idx, "PageFields", [
                          ...permit.PageFields.slice(0, pfIdx),
                          { ...pf, Headers: e.target.value },
                          ...permit.PageFields.slice(pfIdx + 1),
                        ])
                      }
                    />

                    {/* Type */}
                    <select
                      className="form-input"
                      value={pf.Type}
                      onChange={(e) =>
                        handleUpdate(idx, "PageFields", [
                          ...permit.PageFields.slice(0, pfIdx),
                          { ...pf, Type: e.target.value },
                          ...permit.PageFields.slice(pfIdx + 1),
                        ])
                      }
                    >
                      <option value="">Select Type</option>
                      {fieldTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>

                    {/* Options (comma separated) */}
                    <input
                      type="text"
                      placeholder="Options (comma separated)"
                      className="form-input"
                      value={(pf.Options || []).join(", ")}
                      onChange={(e) =>
                        handleUpdate(idx, "PageFields", [
                          ...permit.PageFields.slice(0, pfIdx),
                          {
                            ...pf,
                            Options: e.target.value
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean),
                          },
                          ...permit.PageFields.slice(pfIdx + 1),
                        ])
                      }
                    />

                    {/* Remove field */}
                    <button
                      type="button"
                      className="btn-editForm"
                      onClick={() => handleRemovePageField(idx, pfIdx)}
                      title="Remove field"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-editForm btn-editForm-primary"
                  onClick={() => handleAddPageField(idx)}
                >
                  + Add Page Field
                </button>
              </div>

              {/* Remove Permit Type */}
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn-editForm"
                  onClick={() => handleRemovePermitType(idx)}
                >
                  🗑 Remove Permit Type
                </button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          className="btn-editForm btn-editForm-primary"
          onClick={handleAddPermitType}
        >
          + Add Permit Type
        </button>
      </div>
    );
  });







const FieldRenderer = ({
  col,
  formData,
  dropdownOptions,
  decryptedValue,
  onChange,
  setShowPasswordChange,
  handleDecrypt,
  isCreate,
  modelName,
}) => {
  const value = getNestedValue(formData, col.field);
  const baseField = col.field.split(".")[0];

  // local state
  const [showPopup, setShowPopup] = useState(false);
  const [uploadingMap, setUploadingMap] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [calculatedValue, setCalculatedValue] = useState(value || 0);
  const setUploading = useCallback(
    (key, val) => setUploadingMap((prev) => ({ ...prev, [key]: val })),
    [],
  );

  // ---------- helpers ----------
  const getFileName = (url = "") => {
    const clean = (url || "").split("?")[0].split("#")[0];
    const last = clean.split("/").pop() || "";
    return last || "file";
  };

  // ---------- ImageLink ----------
  const ImageLinkField = React.memo(function ImageLinkField({
    label,
    value,
    onChange,
    uploadApi,
    folder = "",
  }) {
    const [urlInput, setUrlInput] = React.useState(value || "");
    const [broken, setBroken] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);

    React.useEffect(() => {
      setUrlInput(value || "");
      setBroken(false);
    }, [value]);

    const applyUrl = React.useCallback(() => {
      onChange(
        typeof urlInput === "string" ? urlInput.trim() : String(urlInput || ""),
      );
      setBroken(false);
    }, [onChange, urlInput]);

    const handleFile = React.useCallback(
      async (file) => {
        if (!file) return;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("image", file);
          if (folder) fd.append("folder", folder);

          const res = await fetch(uploadApi, { method: "POST", body: fd });
          const text = await res.text().catch(() => "");
          let json = {};
          try {
            json = text ? JSON.parse(text) : {};
          } catch {}
          if (!res.ok)
            throw new Error(
              json?.message || json?.error || text || "Upload failed",
            );

          const newUrl =
            json.fileUrl ||
            json.url ||
            (Array.isArray(json.urls) && json.urls[0]) ||
            (/^https?:\/\//i.test(text.trim()) ? text.trim() : "");

          if (!newUrl) throw new Error("Upload succeeded but no URL returned");
          onChange(newUrl);
          setUrlInput(newUrl);
          setBroken(false);
        } catch (e) {
          alert(e.message || "Upload failed");
        } finally {
          setUploading(false);
        }
      },
      [folder, onChange, uploadApi],
    );

    return (
      <div className="form-group">
        <label className="form-label">{label}</label>

        {/* Preview */}
        {value ? (
          <>
            <div className="flex flex-col items-start">
              <img
                src={value}
                alt={label}
                className="max-h-64 w-full cursor-zoom-in rounded-lg object-contain border border-gray-200 bg-gray-50"
                onClick={() => setShowPreview(true)}
                onError={() => setBroken(true)}
                onLoad={() => setBroken(false)}
              />

              {broken && (
                <span className="mt-2 text-sm text-red-600">
                  Image failed to load
                </span>
              )}
            </div>

            {/* Full-screen preview modal */}
            {showPreview && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
                onClick={() => setShowPreview(false)}
              >
                <img
                  src={value}
                  alt={label}
                  className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain cursor-zoom-out"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">No image available</p>
        )}

        {/* Controls */}
        <div className="mt-4 flex flex-col gap-3">
          {/* URL input + apply button */}
          {/* <div className="flex flex-col sm:flex-row gap-2 w-full">
            <input
              type="url"
              placeholder="Paste image URL…"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            />
            <button
              type="button"
              onClick={applyUrl}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              disabled={!urlInput || uploading}
            >
              {uploading ? "Applying…" : "Apply URL"}
            </button>
          </div> */}

          {/* File upload */}
          <div className="flex flex-col sm:flex-row items-center gap-2 text-sm text-gray-600">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={uploading}
              className="text-sm"
            />
          </div>
        </div>
      </div>
    );
  });

  // ---------- Geolocation ----------
  const parseLatLngFromString = (raw) => {
    if (!raw) return { lat: null, lng: null };
    const m = String(raw).match(/(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)/);
    if (!m) return { lat: null, lng: null };
    return { lat: parseFloat(m[1]), lng: parseFloat(m[3]) };
  };
  const isValidLatLng = (lat, lng) =>
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180;

  const GeolocationField = React.memo(function GeolocationField({
    label,
    value,
    onChange,
    zoom = 15,
  }) {
    const parseLatLngFromString = React.useCallback((raw) => {
      if (!raw) return { lat: null, lng: null };
      const m = String(raw).match(/(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)/);
      if (!m) return { lat: null, lng: null };
      return { lat: parseFloat(m[1]), lng: parseFloat(m[3]) };
    }, []);

    const isValidLatLng = React.useCallback(
      (lat, lng) =>
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180,
      [],
    );

    const initial = parseLatLngFromString(value);
    const [lat, setLat] = React.useState(
      Number.isFinite(initial.lat) ? String(initial.lat) : "",
    );
    const [lng, setLng] = React.useState(
      Number.isFinite(initial.lng) ? String(initial.lng) : "",
    );
    const [error, setError] = React.useState("");

    React.useEffect(() => {
      const { lat: L, lng: G } = parseLatLngFromString(value);
      setLat(Number.isFinite(L) ? String(L) : "");
      setLng(Number.isFinite(G) ? String(G) : "");
      setError("");
    }, [parseLatLngFromString, value]);

    const apply = React.useCallback(() => {
      const nLat = parseFloat(lat);
      const nLng = parseFloat(lng);
      if (!isValidLatLng(nLat, nLng)) {
        setError(
          "Please enter a valid latitude (-90..90) and longitude (-180..180).",
        );
        return;
      }
      setError("");
      onChange(`(${nLat}, ${nLng})`);
    }, [isValidLatLng, lat, lng, onChange]);

    const useDeviceLocation = React.useCallback(() => {
      if (!navigator.geolocation) {
        setError("Geolocation is not supported by this browser.");
        return;
      }
      setError("");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const nLat = Number(pos.coords.latitude.toFixed(6));
          const nLng = Number(pos.coords.longitude.toFixed(6));
          setLat(String(nLat));
          setLng(String(nLng));
          onChange(`(${nLat}, ${nLng})`);
        },
        (err) => setError(err.message || "Failed to get device location."),
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }, [onChange]);

    const nLat = parseFloat(lat);
    const nLng = parseFloat(lng);
    const showMap = isValidLatLng(nLat, nLng);

    return (
      <div className="form-group">
        <label className="form-label">{label}</label>

        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}
        >
          <input
            type="number"
            step="0.000001"
            placeholder="Latitude"
            className="form-input"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            style={{ minWidth: 140 }}
          />
          <input
            type="number"
            step="0.000001"
            placeholder="Longitude"
            className="form-input"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            style={{ minWidth: 160 }}
          />
          <button
            type="button"
            className="btn-editForm btn-editForm-primary"
            onClick={apply}
          >
            Apply
          </button>
          <button
            type="button"
            className="btn-editForm"
            onClick={useDeviceLocation}
          >
            Use current location
          </button>
          <button
            type="button"
            className="btn-editForm"
            onClick={() => {
              setLat("");
              setLng("");
              onChange("");
            }}
          >
            Clear
          </button>
        </div>

        {error && (
          <div style={{ color: "#b00020", fontSize: 13, marginBottom: 8 }}>
            {error}
          </div>
        )}

        {showMap ? (
          <>
            <div style={{ height: 300, borderRadius: 10, overflow: "hidden" }}>
              <iframe
                title="Static Map"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src={`https://www.google.com/maps?q=${nLat},${nLng}&z=${zoom}&output=embed`}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${nLat},${nLng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Google Maps
              </a>
            </div>
          </>
        ) : (
          <p style={{ color: "gray" }}>Invalid or missing location</p>
        )}
      </div>
    );
  });

  // Normalize: only for the Work Permit model
  const isWorkPermit =
    String(modelName || "")
      .toLowerCase()
      .replace(/\s+/g, "") === "workpermit";

  // Decode &amp; etc. (safe in browser)
  const decodeHtml = (s) => {
    if (typeof s !== "string") return s;
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  };

  useEffect(() => {
    if (!col.calculate) return;

    const operands = [];
    Object.keys(col).forEach((k) => {
      if (!isNaN(k)) {
        const refField = col[k];
        const val = parseFloat(getNestedValue(formData, refField)) || 0;
        operands.push(val);
      }
    });

    let result = 0;
    if (col.calculate === "multiply") {
      result = operands.reduce((acc, n) => acc * n, 1);
    } else if (col.calculate === "add") {
      result = operands.reduce((acc, n) => acc + n, 0);
    } else if (col.calculate === "subtract") {
      result = operands.slice(1).reduce((acc, n) => acc - n, operands[0] || 0);
    } else if (col.calculate === "divide") {
      result = operands
        .slice(1)
        .reduce((acc, n) => (n !== 0 ? acc / n : acc), operands[0] || 0);
    }

    setCalculatedValue(result);

    if (value !== result) {
      onChange(baseField, result);
    }
  }, [
    col.calculate,
    getNestedValue(formData, col["1"]),
    getNestedValue(formData, col["2"]),
    value,
    baseField,
    onChange,
  ]);

  // ---------- Renders by type ----------
  if (col.type === "boolean") {
    const checked = !!value;
    return (
      <div className="form-group boolean-field">
        <label className="form-label">{col.label}</label>
        <label className="form-toggle">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(baseField, e.target.checked)}
          />
        </label>
      </div>
    );
  }

  if (col.type === "password") {
    // Create mode: show a regular password input
    if (isCreate) {
      const baseField = col.field.split(".")[0];
      const val = (formData && formData[baseField]) || "";
      return (
        <div className="form-group">
          <label className="form-label">{col.label}</label>
          <input
            type="password"
            className="form-input"
            value={val}
            onChange={(e) => onChange(baseField, e.target.value)}
            placeholder="Enter password"
            autoComplete="new-password"
          />
        </div>
      );
    }

    // Edit mode: keep the change-password flow
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <button
          className="btn-editForm-password-change"
          onClick={() => setShowPasswordChange(true)}
        >
          Change Password
        </button>
      </div>
    );
  }

  if (col.type === "plainPassword") {
    // Hide in create mode
    if (isCreate) return null;

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <input
          className="form-input"
          value={decryptedValue || "••••••••"}
          readOnly
        />
        <button
          className="btn-editForm-password-change"
          onClick={handleDecrypt}
        >
          Decrypt
        </button>
      </div>
    );
  }

  if (col.type === "dropdown") {
    const selected = formData[baseField];
    const rawOptions =
      dropdownOptions?.[col.field] ?? dropdownOptions?.[col.api] ?? [];

    const apiOptions = Array.isArray(rawOptions)
      ? rawOptions
      : Array.isArray(rawOptions.data)
        ? rawOptions.data
        : [];
    const staticOptions = col.options || [];

    const selectedId =
      selected && typeof selected === "object" ? selected._id : selected || "";

    const selectedOption =
      apiOptions.find((opt) => opt._id === selectedId) || null;

    const selectedOptionLabel = col.apiTitle
      ? selectedOption?.[col.apiTitle]
      : staticOptions.includes(selected)
        ? selected
        : "No selection";

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        {/* Chip */}
        <div className="chip-container">
          <span className="form-chip">
            {selectedOptionLabel || "No selection"}
          </span>
        </div>

        {/* Dropdown */}
        <select
          className="form-input"
          value={selectedId || ""}
          onChange={(e) => onChange(baseField, e.target.value)}
        >
          <option value="">-- Select {col.label} --</option>
          {col.apiTitle &&
            apiOptions.map((opt) => (
              <option key={opt._id} value={opt._id}>
                {opt[col.apiTitle]}
              </option>
            ))}
          {!col.apiTitle &&
            staticOptions.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
        </select>

        {/* 🔹 Details Box */}
        {col.details === "True" && selectedOption && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white shadow-sm">
            <ul className="divide-y divide-gray-200">
              {col.BoxShow?.map((fieldKey) => {
                const value = selectedOption[fieldKey];

                let displayValue;
                if (value == null) {
                  displayValue = "—";
                } else if (typeof value === "object") {
                  if (value.roleName) {
                    displayValue = value.roleName;
                  } else if (value.name) {
                    displayValue = value.name;
                  } else {
                    displayValue = JSON.stringify(value);
                  }
                } else {
                  displayValue = value;
                }

                return (
                  <li
                    key={fieldKey}
                    className="px-4 py-3 text-sm sm:flex sm:items-center sm:justify-between"
                  >
                    <div className="font-medium text-gray-600 sm:w-1/3">
                      {fieldKey}
                    </div>
                    <div className="mt-1 text-gray-900 sm:mt-0 sm:w-2/3 break-words">
                      {displayValue}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // helper: show values nicely in details box
  const getNiceValue = (value) => {
    if (value == null) return "—";
    if (typeof value === "object") {
      if (value.roleName) return value.roleName;
      if (value.name) return value.name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  if (col.type === "checkbox" && col.array === "true" && col.api) {
    // ✅ baseField: "assignedTo" from "assignedTo.name"
    const baseField = String(col.field || "").split(".")[0];

    // ✅ selected values can be IDs or objects -> normalize to IDs
    const selectedValues = Array.isArray(formData[baseField])
      ? formData[baseField]
          .map((v) => (typeof v === "object" ? v?._id : v))
          .filter(Boolean)
      : [];

    // ✅ load API options
    const rawOptions =
      dropdownOptions?.[col.field] ?? dropdownOptions?.[col.api] ?? [];

    const apiOptions = Array.isArray(rawOptions)
      ? rawOptions
      : Array.isArray(rawOptions.data)
        ? rawOptions.data
        : [];

    // ✅ build chips (labels)
    const selectedLabels = selectedValues.map((id) => {
      const fromApi = apiOptions.find((opt) => opt._id === id);

      // fallback: from existing formData objects
      const fromForm =
        Array.isArray(formData[baseField]) &&
        formData[baseField].find((v) => typeof v === "object" && v?._id === id);

      const item = fromApi || fromForm || null;

      return {
        _id: id,
        label:
          (item && (item[col.apiTitle] || item.name)) ||
          `(${String(id).slice(0, 6)}...)`,
        item, // keep full object for details rendering
      };
    });

    // ✅ build selected objects for Details Box (prefer API, fallback to formData)
    const selectedOptionsForDetails = selectedValues
      .map((id) => {
        const fromApi = apiOptions.find((opt) => opt._id === id);
        if (fromApi) return fromApi;

        const fromForm =
          Array.isArray(formData[baseField]) &&
          formData[baseField].find(
            (v) => typeof v === "object" && v?._id === id,
          );

        return fromForm || null;
      })
      .filter(Boolean);

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <div className="chip-container">
          {selectedLabels.length > 0 ? (
            selectedLabels.map((item) => (
              <span key={item._id} className="form-chip">
                {item.label}
                <button
                  className="chip-remove"
                  onClick={() => {
                    const newSelected = selectedValues.filter(
                      (v) => v !== item._id,
                    );
                    onChange(baseField, newSelected); // ✅ store in assignedTo
                  }}
                  title="Remove"
                  type="button"
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            <span className="form-chip empty">No selections</span>
          )}
        </div>

        {/* Open popup */}
        <button
          className="btn-editForm-popup-open"
          onClick={() => setShowPopup(true)}
          type="button"
        >
          + Select {col.label}
        </button>

        {showPopup && (
          <MultiSelectApiPopup
            api={col.api}
            apiTitle={col.apiTitle}
            selectedValues={selectedValues}
            onClose={() => setShowPopup(false)}
            onChangeLive={(newIds) => onChange(baseField, newIds)} // ✅ store in assignedTo
          />
        )}

        {/* ✅ Details Box (like dropdown, but for MULTIPLE selected users) */}
        {/* ✅ Details Box (COLLAPSIBLE per selected user) */}
        {col.details === "True" && selectedOptionsForDetails.length > 0 && (
          <div className="mt-4 space-y-3">
            {selectedOptionsForDetails.map((selectedOption) => {
              const title =
                (col.apiTitle && selectedOption?.[col.apiTitle]) ||
                selectedOption?.name ||
                "Selected";

              return (
                <details
                  key={selectedOption._id}
                  className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  {/* Clickable header */}
                  <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-gray-800 bg-gray-50 flex items-center justify-between">
                    <span>{title}</span>
                    <span className="text-xs font-medium text-gray-500">
                      Click to view
                    </span>
                  </summary>

                  {/* Content */}
                  <div className="border-t border-gray-100">
                    <ul className="divide-y divide-gray-200">
                      {col.BoxShow?.map((fieldKey) => (
                        <li
                          key={fieldKey}
                          className="px-4 py-3 text-sm sm:flex sm:items-center sm:justify-between"
                        >
                          <div className="font-medium text-gray-600 sm:w-1/3">
                            {fieldKey}
                          </div>
                          <div className="mt-1 text-gray-900 sm:mt-0 sm:w-2/3 break-words">
                            {getNiceValue(selectedOption?.[fieldKey])}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // if (col.type === "checkbox" && col.array === "true" && col.api) {
  //   const selectedValues = Array.isArray(formData[baseField])
  //     ? formData[baseField].map((v) => (typeof v === "object" ? v._id : v))
  //     : [];

  //     const rawOptions =
  //       dropdownOptions?.[col.field] ?? dropdownOptions?.[col.api] ?? [];

  //     const apiOptions = Array.isArray(rawOptions)
  //       ? rawOptions
  //       : Array.isArray(rawOptions.data)
  //       ? rawOptions.data
  //       : [];

  //   const selectedLabels = selectedValues.map((id) => {
  //     const matchedFromAPI = apiOptions.find((opt) => opt._id === id);
  //     if (matchedFromAPI) {
  //       return {
  //         _id: id,
  //         label:
  //           matchedFromAPI[col.apiTitle] || matchedFromAPI.name || "Unnamed",
  //       };
  //     }
  //     const rawItem =
  //       formData[baseField]?.find?.(
  //         (v) => typeof v === "object" && v._id === id
  //       ) || null;
  //     return {
  //       _id: id,
  //       label:
  //         rawItem?.[col.apiTitle] ||
  //         rawItem?.name ||
  //         `(${String(id).slice(0, 6)}...)`,
  //     };
  //   });

  //   return (
  //     <div className="form-group">
  //       <label className="form-label">{col.label}</label>

  //       <div className="chip-container">
  //         {selectedLabels.length > 0 ? (
  //           selectedLabels.map((item) => (
  //             <span key={item._id} className="form-chip">
  //               {item.label}
  //               <button
  //                 className="chip-remove"
  //                 onClick={() => {
  //                   const newSelected = selectedValues.filter(
  //                     (v) => v !== item._id
  //                   );
  //                   onChange(baseField, newSelected);
  //                 }}
  //                 title="Remove"
  //               >
  //                 ×
  //               </button>
  //             </span>
  //           ))
  //         ) : (
  //           <span className="form-chip empty">No selections</span>
  //         )}
  //       </div>

  //       <button
  //         className="btn-editForm-popup-open"
  //         onClick={() => setShowPopup(true)}
  //       >
  //         + Select {col.label}
  //       </button>

  //       {showPopup && (
  //         <MultiSelectApiPopup
  //           api={col.api}
  //           apiTitle={col.apiTitle}
  //           selectedValues={selectedValues}
  //           onClose={() => setShowPopup(false)}
  //           onChangeLive={(newIds) => onChange(baseField, newIds)}
  //         />
  //       )}
  //     </div>
  //   );
  // }

  if (
    col.type === "checkboxPrefilled" &&
    col.array === "true" &&
    Array.isArray(col.checkBox)
  ) {
    const selectedItems = Array.isArray(formData[baseField])
      ? formData[baseField]
      : [];

    const toggle = (item) => {
      const updated = selectedItems.includes(item)
        ? selectedItems.filter((v) => v !== item)
        : [...selectedItems, item];
      onChange(baseField, updated);
    };

    const toggleAll = () => {
      if (selectedItems.length === col.checkBox.length) {
        // deselect all
        onChange(baseField, []);
      } else {
        // select all
        onChange(baseField, [...col.checkBox]);
      }
    };

    const allSelected =
      selectedItems.length === col.checkBox.length && col.checkBox.length > 0;

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        {/* ✅ Select All option */}
        <div className="checkbox-prefilled-item font-semibold mb-1">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          Select All
        </div>

        <div className="checkbox-prefilled-group">
          {col.checkBox.map((item) => (
            <label key={item} className="checkbox-prefilled-item">
              <input
                type="checkbox"
                checked={selectedItems.includes(item)}
                onChange={() => toggle(item)}
              />
              {item}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (
    (col.type === "subString" || col.type === "permitchecklists") &&
    col.subFields?.length
  ) {
    return (
      <ArrayFieldRenderer col={col} formData={formData} onChange={onChange} />
    );
  }

  if (
    (col.type === "permitchecklists" || col.type === "safetychecks") &&
    col.api
  ) {
    return (
      <SafetyChecklistWithPermit
        col={col}
        formData={formData}
        onChange={onChange}
        baseField={col.field}
        dropdownOptions={dropdownOptions}
      />
    );
  }

  if (col.type === "imageLink") {
    return (
      <ImageLinkField
        label={col.label}
        value={value || ""}
        onChange={(newUrl) => onChange(baseField, newUrl)}
        uploadApi={col.uploadApi}
        folder={col.folder || ""}
      />
    );
  }

  if (col.type === "file") {
    const existing = Array.isArray(value) ? value : value ? [value] : [];
    const isUploading = !!uploadingMap[baseField];
    const isArrayField = col.array === "true"; // 👈 easier flag

    const uploadToApi = async (files) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("file", f));

      const res = await fetch(col.uploadApi, { method: "POST", body: fd });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Upload failed");

      // 🔑 Normalize: always return an array
      if (Array.isArray(json.urls)) return json.urls;
      if (json.url) return [json.url];
      if (typeof json === "string" && /^https?:\/\//.test(json)) return [json];
      return [];
    };

    const handleFilePick = async (e) => {
      const picked = Array.from(e.target.files || []);
      if (!picked.length) return;

      // 🚫 block multiple upload if array != true
      if (!isArrayField && picked.length > 1) {
        alert("Only one file is allowed for this field.");
        e.target.value = "";
        return;
      }

      try {
        setUploading(baseField, true);
        const urls = await uploadToApi(picked);

        if (isArrayField) {
          const onlyExistingUrls = existing.filter(
            (x) => typeof x === "string",
          );
          const merged = Array.from(new Set([...onlyExistingUrls, ...urls]));
          onChange(baseField, merged);
        } else {
          onChange(baseField, urls[0] || "");
        }
      } catch (err) {
        console.error("[UPLOAD] error:", err);
        alert(err.message || "File upload failed. Please try again.");
      } finally {
        setUploading(baseField, false);
        e.target.value = "";
      }
    };

    const removeItem = (idx) => {
      if (isArrayField) {
        const next = [...existing];
        next.splice(idx, 1);
        onChange(baseField, next);
      } else {
        onChange(baseField, "");
      }
    };

    const openUrl = (u) => {
      try {
        window.open(encodeURI(decodeURI(u)), "_blank", "noopener,noreferrer");
      } catch {
        window.open(u, "_blank", "noopener,noreferrer");
      }
    };

    // 🔑 Handle manual link add
    const handleManualAdd = (e) => {
      e.preventDefault();
      const input = e.target.elements.linkInput;
      const url = input.value.trim();
      if (!url) return;

      if (isArrayField) {
        const onlyExistingUrls = existing.filter((x) => typeof x === "string");
        const merged = Array.from(new Set([...onlyExistingUrls, url]));
        onChange(baseField, merged);
      } else {
        if (existing.length > 0) {
          alert("Only one file/link allowed for this field.");
          return;
        }
        onChange(baseField, url);
      }
      input.value = "";
    };

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>

        {/* existing files list */}
        {isArrayField && existing.length > 0 && (
          <ul className="file-list">
            {existing.map((item, i) => (
              <li key={i}>
                <div
                  className="file-item"
                  title={getFileName(item)}
                  onClick={() => openUrl(item)}
                >
                  <span className="file-icon" aria-hidden="true">
                    📎
                  </span>
                  <span className="file-name">{getFileName(item)}</span>
                </div>
                <button
                  type="button"
                  className="file-remove"
                  onClick={() => removeItem(i)}
                  title="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {!isArrayField && typeof value === "string" && value && (
          <div style={{ margin: "8px 0" }}>
            <div
              className="file-item"
              title={getFileName(value)}
              onClick={() => openUrl(value)}
            >
              <span className="file-icon" aria-hidden="true">
                📎
              </span>
              <span className="file-name">{getFileName(value)}</span>
            </div>
            <button
              type="button"
              onClick={() => onChange(baseField, "")}
              className="file-remove"
              title="Remove"
              aria-label="Remove file"
            >
              ×
            </button>
          </div>
        )}

        {/* file picker */}
        <input
          type="file"
          className="form-input"
          multiple={isArrayField} // ✅ only allow multiple if array=true
          onChange={handleFilePick}
          accept={col.accept || "*/*"}
        />

        {/* manual link input */}
        <form
          onSubmit={handleManualAdd}
          style={{ marginTop: 8, display: "flex", gap: 8 }}
        >
          <input
            type="url"
            name="linkInput"
            placeholder="Paste file link (https://...)"
            className="form-input"
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-sm btn-outline">
            Add
          </button>
        </form>

        {isUploading && (
          <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
            Uploading…
          </div>
        )}
      </div>
    );
  }

  if (col.type === "Date") {
    const formatted = value ? new Date(value).toISOString().split("T")[0] : "";
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <input
          type="date"
          className="form-input"
          value={formatted}
          onChange={(e) => onChange(baseField, e.target.value)}
        />
      </div>
    );
  }

  if (col.type === "geolocation") {
    return (
      <GeolocationField
        label={col.label}
        value={value || ""}
        onChange={(newVal) => onChange(baseField, newVal)}
        zoom={col.zoom || 15}
      />
    );
  }

  if (col.type === "Time") {
    const timeValue = String(value ?? "");
    const match = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    let hour = match ? match[1] : "8";
    let minute = match ? match[2] : "00";
    let period = match ? match[3].toUpperCase() : "AM";

    const pad2 = (v) => String(v).padStart(2, "0");
    const clampHour12 = (v) => {
      const n = parseInt(v, 10);
      return isNaN(n) ? "12" : String(((n - 1 + 12) % 12) + 1);
    };

    const handleChange = (newHour, newMinute, newPeriod) => {
      const h = clampHour12(newHour);
      const m = pad2(newMinute);
      const p = String(newPeriod).toUpperCase() === "PM" ? "PM" : "AM";
      onChange(baseField, `${h}:${m} ${p}`);
    };

    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <div style={{ display: "flex", gap: "10px" }}>
          <select
            className="form-input"
            value={clampHour12(hour)}
            onChange={(e) => handleChange(e.target.value, minute, period)}
          >
            {[...Array(12)].map((_, i) => {
              const h = String(i + 1);
              return (
                <option key={h} value={h}>
                  {h}
                </option>
              );
            })}
          </select>

          <select
            className="form-input"
            value={pad2(minute)}
            onChange={(e) => handleChange(hour, e.target.value, period)}
          >
            {["00", "15", "30", "45"].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={period}
            onChange={(e) => handleChange(hour, minute, e.target.value)}
          >
            {["AM", "PM"].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (col.type === "signature") {
    return (
      <SignatureField
        label={col.label}
        value={value || ""}
        onChange={(newUrl) => onChange(baseField, newUrl)}
        uploadApi={col.uploadApi}
        folder={col.folder || "signatures"}
      />
    );
  }
  if (col.calculate) {
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <input
          type="number"
          className="form-input"
          value={calculatedValue}
          disabled
        />
      </div>
    );
  }

  // before rendering editable field
  if (col.edit === false || col.edit === "false") {
    return (
      <div className="form-group">
        <label className="form-label">{col.label}</label>
        <div className="form-readonly">
          {value && value !== "" ? String(value) : "—"}
        </div>
      </div>
    );
  }

  if (col.type === "Number" || col.type === "number") {
    return (
      <div className="form-group">
        <label className="form-label" htmlFor={col.field}>
          {col.label}
        </label>
        <input
          id={col.field}
          name={col.field}
          type="number"
          className="form-input"
          value={value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;

            // Allow blank (so user can clear field)
            if (raw === "") {
              onChange(col.field, "");
              return;
            }

            // Only accept valid numbers
            const num = Number(raw);
            if (!isNaN(num)) {
              onChange(col.field, num);
            }
          }}
          onKeyDown={(e) => {
            // Block invalid characters like e, E, +, -
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          min={col.min ?? undefined}
          max={col.max ?? undefined}
          step={col.step ?? 1} // default to integers
          placeholder={col.placeholder || ""}
        />
      </div>
    );
  }

  if (col.type === "WorkpermitAddition" && Array.isArray(col.subFields)) {
    return (
      <WorkpermitAdditionField
        col={col}
        formData={formData}
        onChange={onChange}
      />
    );
  }

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={col.field}>
        {col.label}
      </label>

      {col.type === "textarea" ? (
        <textarea
          id={col.field}
          name={col.field}
          className="form-input"
          value={value || ""}
          onChange={(e) => onChange(col.field, e.target.value)} // ✅ full path
          rows={3}
        />
      ) : (
        <input
          id={col.field}
          name={col.field}
          type={col.type || "text"}
          className="form-input"
          value={value || ""}
          onChange={(e) => onChange(col.field, e.target.value)} // ✅ full path
        />
      )}
    </div>
  );
};;

export default FieldRenderer;
